import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { XCircle, ArrowRight, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface CancelamentoRecuperacaoCardProps {
  item: any;
}

export function CancelamentoRecuperacaoCard({ item }: CancelamentoRecuperacaoCardProps) {
  // Buscar último cancelamento
  const cancelamento = item.cancelamentos?.[0];
  
  if (!cancelamento) return null;

  const formatarData = (data: string) => {
    return format(new Date(data), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  };

  const isPrazoExpirado = cancelamento.data_limite_recuperacao 
    ? new Date() > new Date(cancelamento.data_limite_recuperacao)
    : false;

  return (
    <Card className="border-l-4 border-l-destructive">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <XCircle className="h-5 w-5 text-destructive" />
          Cancelamento e Recuperação
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Timeline Visual */}
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            <Badge variant="destructive" className="w-full justify-center">
               Cancelado
            </Badge>
            <p className="text-xs text-muted-foreground text-center">
              {formatarData(cancelamento.cancelado_em)}
            </p>
            {cancelamento.motivo_cancelamento?.titulo && (
              <p className="text-xs font-medium text-center">
                {cancelamento.motivo_cancelamento.titulo}
              </p>
            )}
          </div>
          
          {cancelamento.motivo_cancelamento?.permite_recuperacao && (
            <>
              <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <div className="flex-1 space-y-1">
                {cancelamento.recuperado ? (
                  <>
                    <Badge variant="default" className="w-full justify-center bg-success hover:bg-success">
                       Recuperado
                    </Badge>
                    <p className="text-xs text-muted-foreground text-center">
                      {cancelamento.recuperado_em && formatarData(cancelamento.recuperado_em)}
                    </p>
                  </>
                ) : (
                  <>
                    <Badge variant="outline" className="w-full justify-center border-primary text-primary">
                       Aguardando Análise
                    </Badge>
                    {cancelamento.data_limite_recuperacao && (
                      <p className={`text-xs text-center font-medium ${isPrazoExpirado ? 'text-destructive' : 'text-muted-foreground'}`}>
                        <Clock className="h-3 w-3 inline mr-1" />
                        Prazo: {formatarData(cancelamento.data_limite_recuperacao)}
                      </p>
                    )}
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Observação do Cancelamento */}
        {cancelamento.observacao && (
          <div className="space-y-1 p-3 bg-muted/50 rounded-lg">
            <p className="text-xs font-semibold text-muted-foreground">Justificativa do Cancelamento:</p>
            <p className="text-sm">{cancelamento.observacao}</p>
          </div>
        )}

        {/* Observação da Recuperação */}
        {cancelamento.recuperado && cancelamento.observacao_recuperacao && (
          <div className="space-y-1 p-3 bg-success/5 dark:bg-success/20 rounded-lg">
            <p className="text-xs font-semibold text-success dark:text-success">Justificativa da Recuperação:</p>
            <p className="text-sm text-success dark:text-success">{cancelamento.observacao_recuperacao}</p>
          </div>
        )}

        {/* Aviso de Prazo Expirado */}
        {cancelamento.motivo_cancelamento?.permite_recuperacao && 
         !cancelamento.recuperado && 
         isPrazoExpirado && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive font-medium text-center">
               Prazo de recuperação expirado
            </p>
          </div>
        )}

        {/* Cancelamento Definitivo */}
        {!cancelamento.motivo_cancelamento?.permite_recuperacao && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
            <p className="text-sm text-destructive font-medium text-center">
               Cancelamento Definitivo - Não permite recuperação
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
